import React from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Heart, 
  TrendingUp, 
  Shield, 
  Calendar, 
  Phone,
  DollarSign,
  Clock,
  Award,
  Home,
  Wrench,
  Briefcase,
  GraduationCap,
  Star
} from 'lucide-react';

const CareersPage = () => {
  const whyWorkWithUs = [
    {
      icon: DollarSign,
      title: "Competitive Pay",
      description: "Above-market wages with performance incentives and merit-based raises"
    },
    {
      icon: TrendingUp,
      title: "Career Growth",
      description: "Clear advancement paths and professional development opportunities"
    },
    {
      icon: Clock,
      title: "Flexible Environment",
      description: "Work with a supportive team that values your expertise"
    },
    {
      icon: Award,
      title: "Quality Focus",
      description: "Join a company known for excellence and professional craftsmanship"
    }
  ];

  const positions = [
    {
      title: "Outdoor Sales Representative",
      type: "Full-Time",
      location: "Eastern NC",
      experience: "Outdoor sales experience required",
      description: "Drive business growth through direct sales to homeowners. Must have proven outdoor sales experience and strong communication skills.",
      requirements: [
        "Proven outdoor sales experience",
        "Excellent communication skills", 
        "Self-motivated and results-driven",
        "Valid driver's license and reliable vehicle",
        "Ability to work independently"
      ]
    },
    {
      title: "Wood Craftsman/Carpenter",
      type: "Full-Time", 
      location: "Rocky Mount, NC",
      experience: "High skill level required",
      description: "Skilled carpenter for custom roofing and wood construction projects. Must have own professional tools and portfolio of work.",
      requirements: [
        "Advanced carpentry skills",
        "Own professional tool set",
        "Portfolio of completed work",
        "5+ years construction experience",
        "Attention to detail and quality craftsmanship"
      ]
    },
    {
      title: "Commercial Sales Representative",
      type: "Full-Time",
      location: "Eastern NC", 
      experience: "Commercial sales experience preferred",
      description: "Develop and manage commercial roofing accounts. Work with property managers, contractors, and business owners.",
      requirements: [
        "Commercial sales background preferred",
        "B2B relationship building skills",
        "Understanding of commercial construction",
        "Professional presentation skills",
        "CRM software experience"
      ]
    },
    {
      title: "Commercial Roofer",
      type: "Full-Time",
      location: "Eastern NC",
      experience: "Commercial roofing experience required", 
      description: "Install and repair commercial roofing systems including TPO, EPDM, and metal roofing on commercial buildings.",
      requirements: [
        "Commercial roofing experience",
        "Knowledge of multiple roofing systems",
        "Safety certification preferred",
        "Ability to work at heights",
        "Own basic tools and equipment"
      ]
    },
    {
      title: "Experienced Recruiter",
      type: "Full-Time",
      location: "Rocky Mount, NC",
      experience: "Recruiting experience required",
      description: "Build our team by recruiting top talent in sales and construction. Manage full recruitment cycle from sourcing to hiring.",
      requirements: [
        "Proven recruiting experience",
        "Construction industry knowledge preferred", 
        "Strong networking abilities",
        "Interview and assessment skills",
        "Applicant tracking system experience"
      ]
    }
  ];

  const values = [
    {
      icon: Heart,
      title: "Family First",
      description: "We prioritize work-life balance and treat our team like family"
    },
    {
      icon: Award,
      title: "Excellence",
      description: "We take pride in delivering the highest quality workmanship"
    },
    {
      icon: Users,
      title: "Teamwork",
      description: "Success comes from working together and supporting each other"
    },
    {
      icon: Star,
      title: "Integrity",
      description: "We do the right thing, even when no one is watching"
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Careers at Carolina Trophy Roofs | Join Our Team | Eastern NC Jobs</title>
        <meta name="description" content="Join the Carolina Trophy Roofs team! We're hiring outdoor sales reps, skilled carpenters, commercial roofers, and recruiters in Eastern NC. Competitive pay and growth opportunities." />
        <meta name="keywords" content="sales jobs, carpenter jobs, commercial roofing careers, recruiter positions, Rocky Mount jobs, Greenville jobs, construction jobs Eastern NC" />
        <link rel="canonical" href="https://www.carolinatrophyroofs.com/careers" />
        
        <meta property="og:title" content="Careers at Carolina Trophy Roofs | Join Our Team" />
        <meta property="og:description" content="Build your career with Eastern NC's premier roofing company. Competitive pay and growth opportunities." />
        <meta property="og:url" content="https://www.carolinatrophyroofs.com/careers" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-brand-charcoal-gray to-gray-800 text-white py-20 md:py-28">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1920&h=1080&fit=crop&auto=format&q=75" 
            alt="Roofing team at work" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative container mx-auto px-4 z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-brand-safety-orange text-white px-4 py-2 text-sm font-semibold">
              NOW HIRING
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-brand-trophy-gold">
              Build Your Career With Us
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl mx-auto">
              Join Eastern North Carolina's premier roofing company. We offer competitive pay, growth opportunities, and a supportive team environment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold px-8 py-4">
                  <Briefcase className="mr-2 h-5 w-5" />
                  Apply Now
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold/10">
                <Phone className="mr-2 h-5 w-5" />
                Call (252) 302-2003
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          
          {/* Why Work With Us */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-4 text-brand-charcoal-gray">
              Why Work With Carolina Trophy Roofs?
            </h2>
            <p className="text-xl text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              We're not just another roofing company - we're a family-owned business that invests in our people
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyWorkWithUs.map((benefit, index) => (
                <Card key={index} className="border-2 border-gray-200 hover:border-brand-trophy-gold/30 transition-all duration-300 hover:shadow-lg text-center">
                  <CardHeader className="pb-4">
                    <div className="w-16 h-16 bg-brand-trophy-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <benefit.icon className="h-8 w-8 text-brand-trophy-gold" />
                    </div>
                    <CardTitle className="text-xl font-bold text-brand-charcoal-gray">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Company Values */}
          <div className="mb-16 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 md:p-12">
            <h2 className="text-4xl font-bold text-center mb-12 text-brand-charcoal-gray">
              Our Values
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-brand-safety-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-8 w-8 text-brand-safety-orange" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-brand-charcoal-gray">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Open Positions */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-4 text-brand-charcoal-gray">
              Current Openings
            </h2>
            <p className="text-xl text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Join our team and build a rewarding career in the roofing industry
            </p>
            
            <div className="space-y-6">
              {positions.map((position, index) => (
                <Card key={index} className="border-2 border-gray-200 hover:border-brand-trophy-gold/30 transition-all duration-300">
                  <CardHeader>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div>
                        <CardTitle className="text-2xl font-bold text-brand-charcoal-gray mb-2">
                          {position.title}
                        </CardTitle>
                        <div className="flex flex-wrap gap-2">
                          <Badge className="bg-brand-safety-orange text-white">
                            {position.type}
                          </Badge>
                          <Badge variant="outline" className="border-brand-trophy-gold text-brand-charcoal-gray">
                            {position.location}
                          </Badge>
                          <Badge variant="outline">
                            {position.experience}
                          </Badge>
                        </div>
                      </div>
                      <Link to="/contact">
                        <Button className="bg-brand-charcoal-gray hover:bg-brand-charcoal-gray/90 text-white">
                          <Briefcase className="mr-2 h-4 w-4" />
                          Apply Now
                        </Button>
                      </Link>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-4">{position.description}</p>
                    <div>
                      <h4 className="font-semibold text-brand-charcoal-gray mb-2">Requirements:</h4>
                      <ul className="list-disc list-inside space-y-1 text-gray-600">
                        {position.requirements.map((req, reqIndex) => (
                          <li key={reqIndex}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* SEO Resources & Citations */}
          <Card className="mb-16 border-2 border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-white">
              <CardTitle className="flex items-center text-2xl font-bold text-brand-charcoal-gray">
                <div className="w-10 h-10 bg-brand-trophy-gold/10 rounded-full flex items-center justify-center mr-4">
                  <GraduationCap className="h-6 w-6 text-brand-trophy-gold" />
                </div>
                Career Development Resources
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-600 mb-6">
                Our hiring practices and career development programs are informed by industry-leading research and best practices. 
                These resources provide valuable insights for career growth in construction and sales.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-brand-charcoal-gray mb-3">Career & Employment Resources</h4>
                  <ul className="space-y-2 text-sm">
                    <li>
                      <a href="https://www.indeed.com/career-advice/finding-a-job/construction-jobs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Indeed - Construction Industry Employment Trends
                      </a>
                    </li>
                    <li>
                      <a href="https://www.linkedin.com/jobs/sales-jobs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        LinkedIn - Sales Career Development Resources
                      </a>
                    </li>
                    <li>
                      <a href="https://www.glassdoor.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Glassdoor - Roofing Industry Reviews and Salaries
                      </a>
                    </li>
                    <li>
                      <a href="https://www.monster.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Monster.com - Construction Sales Careers
                      </a>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-brand-charcoal-gray mb-3">Professional Development & Industry Standards</h4>
                  <ul className="space-y-2 text-sm">
                    <li>
                      <a href="https://hbr.org/2020/03/the-b2b-sales-revolution" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Harvard Business Review - The Future of B2B Sales
                      </a>
                    </li>
                    <li>
                      <a href="https://www.bls.gov/ooh/sales/wholesale-and-manufacturing-sales-representatives.htm" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Bureau of Labor Statistics - Sales Representatives Outlook
                      </a>
                    </li>
                    <li>
                      <a href="https://www.nasp.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        National Association of Sales Professionals - Industry Standards
                      </a>
                    </li>
                    <li>
                      <a href="https://www.dalecarnegie.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Dale Carnegie Training - Sales Effectiveness Research
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-6">
                <h4 className="font-semibold text-brand-charcoal-gray mb-3">Job Search & Hiring Resources</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-sm">
                    <li>
                      <a href="https://www.careerbuilder.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        CareerBuilder - Roofing Industry Opportunities
                      </a>
                    </li>
                    <li>
                      <a href="https://www.ziprecruiter.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        ZipRecruiter - Sales Position Analysis
                      </a>
                    </li>
                  </ul>
                  <ul className="space-y-2 text-sm">
                    <li>
                      <a href="https://www.shrm.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Society for Human Resource Management - Best Practices in Hiring
                      </a>
                    </li>
                    <li>
                      <a href="https://www.eeoc.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Equal Employment Opportunity Commission - Fair Hiring Practices
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-gray-700">
                  <strong>Disclaimer:</strong> These external resources are provided for informational purposes. 
                  Carolina Trophy Roofs is not responsible for the content of external websites. 
                  For specific career opportunities and requirements, please contact our team directly.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Employee Benefits */}
          {/* Application Process */}
          <div className="mb-16 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 md:p-12">
            <h2 className="text-4xl font-bold text-center mb-12 text-brand-charcoal-gray">
              How to Apply
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: "1",
                  title: "Submit Application",
                  description: "Contact us with your resume and experience details"
                },
                {
                  step: "2", 
                  title: "Interview Process",
                  description: "Meet with our team to discuss the opportunity"
                },
                {
                  step: "3",
                  title: "Start Your Career",
                  description: "Begin your journey with comprehensive training"
                }
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-brand-safety-orange rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-2xl">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-brand-charcoal-gray">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <Card className="bg-gradient-to-r from-brand-charcoal-gray to-gray-800 text-white">
            <CardContent className="p-8 md:p-12 text-center">
              <div className="w-16 h-16 bg-brand-trophy-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <GraduationCap className="h-8 w-8 text-brand-trophy-gold" />
              </div>
              <h2 className="text-3xl font-bold mb-4 text-brand-trophy-gold">
                Ready to Start Your Roofing Career?
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-200">
                Join a company that values your expertise, rewards your hard work, and offers real growth opportunities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold px-8 py-4">
                    <Briefcase className="mr-2 h-5 w-5" />
                    Apply Today
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold/10">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (252) 302-2003
                </Button>
              </div>
              <div className="mt-8 text-sm text-gray-300">
                <p>Carolina Trophy Roofs is an Equal Opportunity Employer</p>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </Layout>
  );
};

export default CareersPage;